import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QuotationSnapshot, QuotationItem } from '../../../shared/api/quotations.api'
import type { RequestItem } from '../../../shared/api/types'
import { bulkUpdateDealStatus, bulkClearDealStatus } from '../../../shared/api/requestItems.api'
import { toastOrderAfterClear, toastOrderAfterDeal } from './dealOrderToast'
import { KPDetailsTable } from './KPDetailsTable'
import { kpVatView } from '../utils/kpVat'
import { KpVatBadge } from './KpVatBadge'

const MATRIX_LABELS: Record<string, string> = {
  stock: 'Склад',
  china: 'Китай',
  rf: 'РФ',
  manuf: 'Производители',
  other: 'Стоки',
}

// Матрица КП → sourceType квоты (для подбора заметки выбранной квоты).
const SOURCE_BY_MATRIX: Record<string, string> = {
  stock: 'STOCK',
  china: 'CHINA',
  rf: 'RF',
  manuf: 'MANUFACTURER',
  other: 'OTHER',
}

// Статусы позиций, которые трактуются как «финальные из сделки».
type DealStatus = 'ORDERED' | 'REJECTED_BY_CUSTOMER'

interface Props {
  requestId: string
  detail: QuotationSnapshot
  requestItems: RequestItem[]
  onClose: () => void
  onDownload: (snapshot: QuotationSnapshot) => void
}

export function KPDetailsModal({ requestId, detail, requestItems, onClose, onDownload }: Props) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [matrixFilter, setMatrixFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const kpItems = useMemo(() => (detail.items ?? []) as QuotationItem[], [detail.items])

  // Мапа itemId → исход позиции **в этом КП** (не глобальный статус).
  // С 28.04 (bug_3g9zlnfv5qgk): раньше брали request_item.status — после
  // оформления той же позиции через другой КП в КП №1 показывался
  // неправильный статус («В заказ» вместо «Отказ клиента»).
  // Теперь смотрим orderedFromQuotationId / rejectedFromQuotationId
  // и сравниваем с detail.id — каждый КП помнит свой исход.
  const itemStatusById = useMemo(() => {
    const m = new Map<string, string>()
    for (const it of requestItems) {
      if (it.orderedFromQuotationId === detail.id) {
        m.set(it.id, 'ORDERED')
      } else if (it.rejectedFromQuotationId === detail.id) {
        m.set(it.id, 'REJECTED_BY_CUSTOMER')
      }
      // Иначе позиция в этом КП ничего не решала — статус пустой.
    }
    return m
  }, [requestItems, detail.id])

  // Заметка квоты по позиции для колонки «Заметка». Снапшот КП заметку не
  // хранит — подтягиваем из текущих квот позиции: ищем квоту той же матрицы и
  // поставщика (строка КП), берём первую с непустой заметкой. Если строки КП
  // больше нет точного поставщика — любую квоту той же матрицы.
  const noteById = useMemo(() => {
    const m = new Map<string, string | null>()
    const itemById = new Map(requestItems.map((ri) => [ri.id, ri]))
    for (const it of kpItems) {
      const ri = itemById.get(it.itemId)
      if (!ri?.results?.length) continue
      const src = SOURCE_BY_MATRIX[it.matrix]
      const sameMatrix = ri.results.filter((r) => r.sourceType === src)
      const exact = sameMatrix.filter((r) => r.supplierName === it.supplierName)
      const pool = exact.length ? exact : sameMatrix
      const withNote = pool.find((r) => (r.note ?? '').trim().length > 0)
      m.set(it.itemId, (withNote ?? pool[0])?.note ?? null)
    }
    return m
  }, [kpItems, requestItems])

  // Фильтры: поиск (partNumber/brand/supplier), матрица, статус.
  const visibleItems = useMemo(() => {
    return kpItems.filter((it) => {
      if (matrixFilter && it.matrix !== matrixFilter) return false
      if (statusFilter) {
        const s = itemStatusById.get(it.itemId) ?? ''
        if (statusFilter === '__none__') {
          if (s === 'ORDERED' || s === 'REJECTED_BY_CUSTOMER') return false
        } else if (s !== statusFilter) return false
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase()
        const hay = [it.partNumber, it.brand ?? '', it.supplierName ?? ''].join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [kpItems, matrixFilter, statusFilter, search, itemStatusById])

  // «Итого» таблицы при активных фильтрах — сумма ТОЛЬКО видимых позиций
  // (раньше всегда показывалась полная сумма КП, что путало при фильтре по
  // статусу «Счёт»/«Отказ»). Без фильтров — каноничная сумма снапшота, чтобы
  // не расходиться на копейки округления с шапкой и Excel.
  const filtersActive = Boolean(matrixFilter || statusFilter || search.trim())
  const visibleSum = useMemo(
    () => (filtersActive ? kpVatView(visibleItems, detail.vatMode).total : detail.totalSum),
    [filtersActive, visibleItems, detail.totalSum, detail.vatMode],
  )

  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every((it) => selected.has(it.itemId))

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      const next = new Set(selected)
      for (const it of visibleItems) next.delete(it.itemId)
      setSelected(next)
    } else {
      const next = new Set(selected)
      for (const it of visibleItems) next.add(it.itemId)
      setSelected(next)
    }
  }

  const toggleOne = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const orderMutation = useMutation({
    mutationFn: () =>
      bulkUpdateDealStatus(requestId, {
        itemIds: Array.from(selected),
        status: 'ORDERED',
        quotationId: detail.id,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['order-by-request', requestId] })
      setSelected(new Set())
      setError(null)
      toastOrderAfterDeal(res)
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось выставить счёт по позициям'
      setError(msg)
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (reason: string) =>
      bulkUpdateDealStatus(requestId, {
        itemIds: Array.from(selected),
        status: 'REJECTED_BY_CUSTOMER',
        // С 28.04 передаём quotationId и при отказе — чтобы бэк записал
        // rejectedFromQuotationId. Без этого детали этого же КП после
        // переоткрытия не помнили бы, что отказ был именно здесь.
        quotationId: detail.id,
        reason,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })
      setSelected(new Set())
      setError(null)
      setShowRejectModal(false)
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось оформить отказ'
      setError(msg)
    },
  })

  // Снятие статуса сделки у выбранных позиций — возврат «как было до».
  const clearMutation = useMutation({
    mutationFn: () =>
      bulkClearDealStatus(requestId, {
        itemIds: Array.from(selected),
        quotationId: detail.id,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })
      void queryClient.invalidateQueries({ queryKey: ['order-by-request', requestId] })
      setSelected(new Set())
      // Если ни у одной выбранной позиции не было статуса этого КП — сообщаем.
      setError(res.updated === 0 ? 'У выбранных позиций нет статуса по этому КП' : null)
      toastOrderAfterClear(res)
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось снять статус'
      setError(msg)
    },
  })

  const isBusy = orderMutation.isPending || rejectMutation.isPending || clearMutation.isPending

  const handleOrder = () => {
    if (selected.size === 0) {
      setError('Выделите позиции чекбоксами')
      return
    }
    setError(null)
    orderMutation.mutate()
  }

  const handleReject = () => {
    if (selected.size === 0) {
      setError('Выделите позиции чекбоксами')
      return
    }
    setError(null)
    setShowRejectModal(true)
  }

  const handleClear = () => {
    if (selected.size === 0) {
      setError('Выделите позиции чекбоксами')
      return
    }
    setError(null)
    clearMutation.mutate()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-[95vw] max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              КП от {formatDate(detail.createdAt)}
            </h3>
            <div className="text-xs text-gray-400 mt-0.5">
              {detail.createdBy
                ? detail.createdBy.name || detail.createdBy.email
                : 'удалённый пользователь'}{' '}
              · Итого{' '}
              <span className="font-mono font-medium text-gray-700">
                {detail.totalSum.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽
              </span>{' '}
              · Позиций: {kpItems.length}
              <KpVatBadge vatMode={detail.vatMode} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Плашки курсов / наценок */}
        <div className="px-5 py-2 border-b border-gray-100 flex flex-wrap gap-3 text-xs">
          {detail.exchangeRates && (
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Курсы:</span>
              {Object.entries(detail.exchangeRates as Record<string, number>).map(([cur, rate]) => (
                <span key={cur} className="bg-gray-100 rounded px-2 py-0.5 font-mono">
                  {cur} {rate}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-gray-400">Наценки:</span>
            {Object.entries(detail.markups as Record<string, number>).map(([k, v]) => (
              <span key={k} className="bg-blue-50 rounded px-2 py-0.5">
                {MATRIX_LABELS[k] ?? k}: ×{v}
              </span>
            ))}
          </div>
        </div>

        {/* Панель фильтров: поиск + select матрица + select статус */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск: артикул / бренд / поставщик"
            className="flex-1 min-w-[220px] border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-400"
          />
          <select
            value={matrixFilter}
            onChange={(e) => setMatrixFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
          >
            <option value="">Все матрицы</option>
            {Object.entries(MATRIX_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
          >
            <option value="">Все статусы</option>
            <option value="__none__">Без статуса</option>
            <option value="ORDERED">Счёт</option>
            <option value="REJECTED_BY_CUSTOMER">Отказ клиента</option>
          </select>
          <div className="text-xs text-gray-500 ml-auto">
            Показано: {visibleItems.length} из {kpItems.length}
            {selected.size > 0 && (
              <span className="ml-3 text-brand-600 font-medium">Выбрано: {selected.size}</span>
            )}
          </div>
        </div>

        {/* Bulk-actions */}
        {selected.size > 0 && (
          <div className="px-5 py-2 border-b border-gray-100 bg-brand-50 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-700">
              Действие для {selected.size} {selected.size === 1 ? 'позиции' : 'позиций'}:
            </span>
            <button
              type="button"
              disabled={isBusy}
              onClick={handleOrder}
              className="px-3 py-1 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              Счёт
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={handleReject}
              className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              Отказ клиента
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={handleClear}
              title="Убрать статус «Счёт»/«Отказ клиента» у выбранных позиций — вернуть как было"
              className="px-3 py-1 text-xs font-medium text-gray-700 border border-gray-300 bg-white rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Снять статус
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                setSelected(new Set())
                setError(null)
              }}
              className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Снять выделение
            </button>
            {error && <span className="text-xs text-red-600 ml-2">{error}</span>}
          </div>
        )}

        {/* Таблица позиций — отдельный компонент с ресайзом колонок */}
        <KPDetailsTable
          items={visibleItems}
          totalSum={visibleSum}
          selected={selected}
          allVisibleSelected={allVisibleSelected}
          onToggleAll={toggleSelectAll}
          onToggleOne={toggleOne}
          statusById={itemStatusById}
          noteById={noteById}
        />

        {/* Футер */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-2">
          <button
            onClick={() => onDownload(detail)}
            className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Скачать КП
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Закрыть
          </button>
        </div>
      </div>

      {showRejectModal && (
        <RejectReasonModal
          count={selected.size}
          onCancel={() => setShowRejectModal(false)}
          onConfirm={(reason) => rejectMutation.mutate(reason)}
          isBusy={rejectMutation.isPending}
        />
      )}
    </div>
  )
}

function RejectReasonModal({
  count,
  onCancel,
  onConfirm,
  isBusy,
}: {
  count: number
  onCancel: () => void
  onConfirm: (reason: string) => void
  isBusy: boolean
}) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  const canSubmit = trimmed.length >= 3

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl border border-gray-200 w-full max-w-md p-5 space-y-3 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900">Причина отказа клиента</h3>
        <p className="text-xs text-gray-500">
          Будет применена к {count} {count === 1 ? 'позиции' : 'позициям'}. Причина допишется в
          комментарий каждой позиции.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Например: дорого, нашли дешевле, не подошла поставка…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isBusy}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={!canSubmit || isBusy}
            onClick={() => onConfirm(trimmed)}
            className="px-3 py-1.5 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            Подтвердить отказ
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export type { DealStatus }
